import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, DollarSign, Building2, Users, Calendar, Scissors, Star } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

const RealEstateManagingPartnerAnalytics = () => {
  const [searchParams] = useSearchParams();
  const template = searchParams.get('template');
  const industry = searchParams.get('industry');
  const role = searchParams.get('role');

  // Show Hair Salon - Barber version
  if ((template === 'Hair Salon' || industry === 'Hair Salon') && role === 'Barber') {
    // Monthly revenue data for salon
    const monthlyRevenueData = [
      { month: 'Jan', revenue: 28.5, expenses: 18.2, profit: 10.3 },
      { month: 'Feb', revenue: 30.2, expenses: 18.8, profit: 11.4 },
      { month: 'Mar', revenue: 32.8, expenses: 19.5, profit: 13.3 },
      { month: 'Apr', revenue: 35.5, expenses: 20.2, profit: 15.3 },
      { month: 'May', revenue: 38.2, expenses: 21.0, profit: 17.2 },
      { month: 'Jun', revenue: 40.5, expenses: 21.8, profit: 18.7 },
      { month: 'Jul', revenue: 42.8, expenses: 22.5, profit: 20.3 },
      { month: 'Aug', revenue: 41.2, expenses: 22.0, profit: 19.2 },
      { month: 'Sep', revenue: 39.8, expenses: 21.5, profit: 18.3 },
      { month: 'Oct', revenue: 37.5, expenses: 20.8, profit: 16.7 },
      { month: 'Nov', revenue: 36.2, expenses: 20.2, profit: 16.0 },
      { month: 'Dec', revenue: 45.8, expenses: 24.5, profit: 21.3 }
    ];

    // Service breakdown data
    const serviceBreakdownData = [
      { name: 'Hair Styling', value: 42, color: '#8B5CF6' },
      { name: 'Color Services', value: 35, color: '#EC4899' },
      { name: 'Nail Care', value: 15, color: '#3B82F6' },
      { name: 'Other Services', value: 8, color: '#10B981' }
    ];

    // Customer satisfaction trend
    const satisfactionTrendData = [
      { month: 'Jan', satisfaction: 92 },
      { month: 'Feb', satisfaction: 93 },
      { month: 'Mar', satisfaction: 94 },
      { month: 'Apr', satisfaction: 95 },
      { month: 'May', satisfaction: 96 },
      { month: 'Jun', satisfaction: 97 },
      { month: 'Jul', satisfaction: 96 },
      { month: 'Aug', satisfaction: 97 },
      { month: 'Sep', satisfaction: 98 },
      { month: 'Oct', satisfaction: 97 },
      { month: 'Nov', satisfaction: 98 },
      { month: 'Dec', satisfaction: 98 }
    ];

    // Stylist performance data
    const stylistPerformanceData = [
      { name: 'Sarah Williams', appointments: 248, revenue: 62.5, satisfaction: 98, department: 'Hair Styling' },
      { name: 'Michael Chen', appointments: 215, revenue: 58.2, satisfaction: 95, department: 'Color Services' },
      { name: 'Emma Johnson', appointments: 198, revenue: 45.8, satisfaction: 97, department: 'Reception' },
      { name: 'Emily Rodriguez', appointments: 192, revenue: 48.5, satisfaction: 94, department: 'Hair Styling' },
      { name: 'Jennifer Lopez', appointments: 175, revenue: 38.2, satisfaction: 96, department: 'Nail Care' }
    ];

    // Peak hours data
    const peakHoursData = [
      { hour: '8AM', bookings: 15 },
      { hour: '9AM', bookings: 28 },
      { hour: '10AM', bookings: 42 },
      { hour: '11AM', bookings: 48 },
      { hour: '12PM', bookings: 35 },
      { hour: '1PM', bookings: 30 },
      { hour: '2PM', bookings: 38 },
      { hour: '3PM', bookings: 45 },
      { hour: '4PM', bookings: 52 },
      { hour: '5PM', bookings: 58 },
      { hour: '6PM', bookings: 48 },
      { hour: '7PM', bookings: 35 }
    ];

    return (
      <div className="min-h-screen bg-black text-white p-6 overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Salon Analytics</h1>
          <p className="text-gray-400">Hair Salon Performance, Revenue & Customer Insights</p>
        </div>

        {/* Top KPI Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Total Monthly Revenue */}
          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Monthly Revenue</p>
                <h2 className="text-3xl font-bold">$45.8K</h2>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign size={24} className="text-green-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +15.2%
              </span>
              <span className="text-gray-400 text-sm">vs last month</span>
            </div>
          </div>

          {/* Total Appointments */}
          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total Appointments</p>
                <h2 className="text-3xl font-bold">1,245</h2>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Calendar size={24} className="text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +8.5%
              </span>
              <span className="text-gray-400 text-sm">this month</span>
            </div>
          </div>

          {/* Customer Satisfaction */}
          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Satisfaction Rate</p>
                <h2 className="text-3xl font-bold">98%</h2>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Star size={24} className="text-blue-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +2.1%
              </span>
              <span className="text-gray-400 text-sm">customer rating</span>
            </div>
          </div>

          {/* Active Clients */}
          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Active Clients</p>
                <h2 className="text-3xl font-bold">892</h2>
              </div>
              <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Users size={24} className="text-pink-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +12%
              </span>
              <span className="text-gray-400 text-sm">new clients</span>
            </div>
          </div>
        </div>

        {/* Revenue & Profit Chart */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4">Monthly Revenue & Profit</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2} name="Revenue ($K)" />
                <Line type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} name="Profit ($K)" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4">Service Revenue Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceBreakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {serviceBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Satisfaction & Peak Hours */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4">Customer Satisfaction Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={satisfactionTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" domain={[90, 100]} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line type="monotone" dataKey="satisfaction" stroke="#3B82F6" strokeWidth={2} name="Satisfaction %" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4">Peak Booking Hours</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={peakHoursData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="bookings" fill="#EC4899" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Performing Stylists Table */}
        <div className="bg-black border border-white rounded-xl p-6 mb-6">
          <h3 className="text-xl font-bold mb-4">Top Performing Stylists</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Stylist Name</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Department</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Appointments</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Revenue ($K)</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Satisfaction</th>
                </tr>
              </thead>
              <tbody>
                {stylistPerformanceData.map((stylist, idx) => (
                  <tr key={idx} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-semibold">{stylist.name}</td>
                    <td className="py-4 px-4 text-gray-400">{stylist.department}</td>
                    <td className="py-4 px-4 text-right font-semibold">{stylist.appointments}</td>
                    <td className="py-4 px-4 text-right text-green-400 font-semibold">${stylist.revenue}</td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-blue-400 font-semibold">{stylist.satisfaction}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Default Real Estate Analytics below
  // Risk trend data over time
  const riskTrendData = [
    { month: 'Jan', financial: 45, operational: 30, market: 25, regulatory: 20 },
    { month: 'Feb', financial: 50, operational: 35, market: 30, regulatory: 22 },
    { month: 'Mar', financial: 48, operational: 40, market: 28, regulatory: 25 },
    { month: 'Apr', financial: 52, operational: 38, market: 35, regulatory: 28 },
    { month: 'May', financial: 55, operational: 42, market: 32, regulatory: 30 },
    { month: 'Jun', financial: 58, operational: 45, market: 38, regulatory: 32 },
    { month: 'Jul', financial: 60, operational: 48, market: 40, regulatory: 35 },
    { month: 'Aug', financial: 55, operational: 46, market: 38, regulatory: 33 },
    { month: 'Sep', financial: 52, operational: 44, market: 35, regulatory: 31 },
    { month: 'Oct', financial: 48, operational: 40, market: 32, regulatory: 28 },
    { month: 'Nov', financial: 45, operational: 38, market: 30, regulatory: 26 },
    { month: 'Dec', financial: 42, operational: 35, market: 28, regulatory: 24 }
  ];

  // Monthly cash flow data
  const cashFlowData = [
    { month: 'Jan', cashFlow: 6.2 },
    { month: 'Feb', cashFlow: 5.8 },
    { month: 'Mar', cashFlow: 6.5 },
    { month: 'Apr', cashFlow: 7.2 },
    { month: 'May', cashFlow: 6.9 },
    { month: 'Jun', cashFlow: 7.5 },
    { month: 'Jul', cashFlow: 7.8 },
    { month: 'Aug', cashFlow: 7.3 },
    { month: 'Sep', cashFlow: 7.9 },
    { month: 'Oct', cashFlow: 8.2 },
    { month: 'Nov', cashFlow: 7.6 },
    { month: 'Dec', cashFlow: 8.5 }
  ];

  // Risk & Contingency Analysis
  const riskContingency = [
    { project: 'Uptown Dallas Tower', risk: 'High', probability: 'Medium', contingency: '$2.5M' },
    { project: 'Frisco Tech Campus', risk: 'Medium', probability: 'Low', contingency: '$1.8M' },
    { project: 'Downtown Office Complex', risk: 'Low', probability: 'Low', contingency: '$0.9M' },
    { project: 'Legacy West Office Park', risk: 'High', probability: 'High', contingency: '$3.2M' }
  ];

  // Profitability Analysis
  const profitabilityData = [
    { property: 'Uptown Dallas Tower', revenue: 47.5, expenses: 32.8, profit: 14.7, roi: '31%', occupancy: '98%' },
    { property: 'Frisco Tech Campus', revenue: 95.0, expenses: 68.5, profit: 26.5, roi: '28%', occupancy: '97%' },
    { property: 'Victory Park Complex', revenue: 88.0, expenses: 65.2, profit: 22.8, roi: '26%', occupancy: '93%' },
    { property: 'Knox Henderson Retail', revenue: 18.5, expenses: 14.2, profit: 4.3, roi: '23%', occupancy: '92%' },
    { property: 'Design District Warehouse', revenue: 28.5, expenses: 22.8, profit: 5.7, roi: '20%', occupancy: '90%' },
    { property: 'Legacy West Office Park', revenue: 72.0, expenses: 52.4, profit: 19.6, roi: '27%', occupancy: '95%' }
  ];

  // Detailed Risk Factors
  const detailedRisks = [
    { category: 'Market Volatility', impact: 'High', probability: 'Medium', mitigation: 'Diversification Strategy' },
    { category: 'Interest Rate Changes', impact: 'High', probability: 'High', mitigation: 'Fixed Rate Locks' },
    { category: 'Tenant Default', impact: 'Medium', probability: 'Low', mitigation: 'Credit Screening' },
    { category: 'Property Maintenance', impact: 'Medium', probability: 'Medium', mitigation: 'Preventive Programs' },
    { category: 'Regulatory Changes', impact: 'Low', probability: 'Low', mitigation: 'Compliance Monitoring' }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-500 bg-red-500/20';
      case 'Medium': return 'text-yellow-500 bg-yellow-500/20';
      case 'Low': return 'text-green-500 bg-green-500/20';
      default: return 'text-gray-500 bg-gray-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="text-gray-400">Commercial Real Estate Portfolio Performance & Risk Analysis</p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Total Budget Allocated */}
        <div className="bg-black border border-gray-400 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-400 text-sm mb-1">Total Budget Allocated</p>
              <h2 className="text-3xl font-bold">$717.5M</h2>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign size={24} className="text-green-500" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
              <TrendingUp size={16} />
              +8.5%
            </span>
            <span className="text-gray-400 text-sm">vs last quarter</span>
          </div>
        </div>

        {/* Total Revenue Expected */}
        <div className="bg-black border border-gray-400 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-400 text-sm mb-1">Total Revenue Expected</p>
              <h2 className="text-3xl font-bold">$425.5M</h2>
            </div>
            <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp size={24} className="text-teal-500" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
              <TrendingUp size={16} />
              +12.3%
            </span>
            <span className="text-gray-400 text-sm">Annual growth</span>
          </div>
        </div>

        {/* Total Expenses Incurred */}
        <div className="bg-black border border-gray-400 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-400 text-sm mb-1">Total Expenses Incurred</p>
              <h2 className="text-3xl font-bold">$318.7M</h2>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Building2 size={24} className="text-purple-500" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">75% of Budget</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Risk Trend Analysis - Takes 2 columns */}
        <div className="col-span-2 bg-black border border-gray-400 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold mb-1">Risk Trend Analysis</h3>
              <p className="text-gray-400 text-sm">Monthly risk assessment across categories</p>
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={riskTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Line type="monotone" dataKey="financial" stroke="#8B5CF6" strokeWidth={2} name="Financial Risk" />
              <Line type="monotone" dataKey="operational" stroke="#EF4444" strokeWidth={2} name="Operational Risk" />
              <Line type="monotone" dataKey="market" stroke="#F59E0B" strokeWidth={2} name="Market Risk" />
              <Line type="monotone" dataKey="regulatory" stroke="#10B981" strokeWidth={2} name="Regulatory Risk" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Right Column - Smaller Cards */}
        <div className="space-y-6">
          {/* Monthly Cash Flow Trends */}
          <div className="bg-black border border-gray-400 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Monthly Cash Flow Trends</h3>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10 }} />
                <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="cashFlow" fill="#10B981" name="Cash Flow ($M)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Risk & Contingency Analysis */}
          <div className="bg-black border border-gray-400 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Risk & Contingency</h3>
            <div className="space-y-3">
              {riskContingency.map((item, idx) => (
                <div key={idx} className="text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-semibold text-xs">{item.project}</span>
                    <span className="text-gray-400 text-xs">{item.contingency}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getRiskColor(item.risk)}`}>
                      {item.risk}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getRiskColor(item.probability)}`}>
                      {item.probability}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Profitability & Risk Details */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Profitability Analysis - Takes 2 columns */}
        <div className="col-span-2 bg-black border border-gray-400 rounded-xl p-6">
          <h3 className="text-xl font-bold mb-4">Profitability Analysis</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-400">
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Property</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Revenue ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Expenses ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Profit ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">ROI</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {profitabilityData.map((property, idx) => (
                  <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                    <td className="py-4 px-2 text-white font-semibold text-sm">{property.property}</td>
                    <td className="py-4 px-2 text-white text-sm">${property.revenue}</td>
                    <td className="py-4 px-2 text-white text-sm">${property.expenses}</td>
                    <td className="py-4 px-2 text-green-500 font-semibold text-sm">${property.profit}</td>
                    <td className="py-4 px-2 text-white text-sm">{property.roi}</td>
                    <td className="py-4 px-2 text-white text-sm">{property.occupancy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Risk Factors */}
        <div className="bg-black border border-gray-400 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-4">Detailed Risk Factors</h3>
          <div className="space-y-3">
            {detailedRisks.map((risk, idx) => (
              <div key={idx} className="p-3 bg-white/5 border border-gray-400/20 rounded-lg">
                <div className="text-white font-semibold text-sm mb-2">{risk.category}</div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getRiskColor(risk.impact)}`}>
                    Impact: {risk.impact}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getRiskColor(risk.probability)}`}>
                    Prob: {risk.probability}
                  </span>
                </div>
                <div className="text-xs text-gray-400">{risk.mitigation}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended Actions Section */}
      <div className="bg-gradient-to-r from-purple-900/50 to-purple-800/50 border border-purple-500/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="text-purple-400" size={24} />
          <h2 className="text-2xl font-bold">Recommended Actions</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Action 1 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Review Legacy West Budget</h4>
                <p className="text-gray-400 text-xs mb-2">High risk with high probability detected. Contingency fund of $3.2M allocated.</p>
                <span className="text-red-400 text-xs font-semibold">URGENT</span>
              </div>
            </div>
          </div>

          {/* Action 2 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-yellow-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Optimize Cash Flow Strategy</h4>
                <p className="text-gray-400 text-xs mb-2">Q4 showing 18% increase. Consider reinvestment opportunities.</p>
                <span className="text-yellow-400 text-xs font-semibold">RECOMMENDED</span>
              </div>
            </div>
          </div>

          {/* Action 3 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-green-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Capitalize on High Occupancy</h4>
                <p className="text-gray-400 text-xs mb-2">Uptown Tower at 98% occupancy. Potential for rate increase.</p>
                <span className="text-green-400 text-xs font-semibold">OPPORTUNITY</span>
              </div>
            </div>
          </div>

          {/* Action 4 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="text-blue-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Review Interest Rate Exposure</h4>
                <p className="text-gray-400 text-xs mb-2">High probability risk identified. Consider fixed-rate locks.</p>
                <span className="text-blue-400 text-xs font-semibold">PRIORITY</span>
              </div>
            </div>
          </div>

          {/* Action 5 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 className="text-purple-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Assess Design District Performance</h4>
                <p className="text-gray-400 text-xs mb-2">20% ROI below portfolio average. Improvement plan needed.</p>
                <span className="text-purple-400 text-xs font-semibold">REVIEW</span>
              </div>
            </div>
          </div>

          {/* Action 6 */}
          <div className="bg-black/50 border border-gray-400/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-teal-500" size={16} />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">Expand Preventive Maintenance</h4>
                <p className="text-gray-400 text-xs mb-2">Medium risk mitigation strategy showing positive results.</p>
                <span className="text-teal-400 text-xs font-semibold">SUGGESTED</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealEstateManagingPartnerAnalytics;
